<script setup lang="ts">
import Dropdown from 'primevue/dropdown'

const usuarioStore = useUsuarioStore()
const { usuarioAtual, cartorioAtual, cartorios } = storeToRefs(usuarioStore)

const selectedCartorio = ref(null)

// Computed para opções de cartório (mostrando todos)
const opcaoCartorios = computed(() => {
  return usuarioAtual.value.cartorios
})

// Manter selectedCartorio sincronizado com cartorioAtual
watch(cartorioAtual, (novoCartorio) => {
  if (novoCartorio && novoCartorio.id) {
    selectedCartorio.value = novoCartorio
  }
}, { immediate: true })

// Computed para o placeholder formatado
const placeholderCartorio = computed(() => {
  return formatarOpcaoCartorio(cartorioAtual.value)
})

// Passthrough para personalizar classes do Dropdown
const dropdownPt = computed(() => ({
  root: {
    class: 'form-select',
  },
  input: {
    class: 'form-control bg-transparent text-white border-white-50',
  },
  trigger: {
    class: 'btn btn-outline-light border-0',
  },
  panel: {
    class: 'dropdown-menu shadow border-0',
  },
  wrapper: {
    class: 'dropdown-menu-wrapper',
  },
  list: {
    class: 'list-unstyled mb-0',
  },
  item: ({ context }) => ({
    class: [
      'dropdown-item d-flex align-items-center px-3 py-2',
      {
        'active text-white': context.selected,
        'text-body': !context.selected,
      }
    ],
    style: context.selected
      ? {
          backgroundColor: usuarioStore.getBgCor(cartorioAtual.value.cor),
        }
      : {},
  }),
  filterContainer: {
    class: 'p-3 border-bottom',
  },
  filter: {
    class: 'form-control',
  },
}))

function formatarOpcaoCartorio (cartorio: any) {
  return usuarioStore.formatarNomeCartorio(cartorio)
}

function aoSelecionarCartorio (event: any) {
  if (event.value && event.value.id) {
    usuarioStore.selecionarCartorio(event.value.id)
  }
}

// Função removida - usando usuarioStore.selecionarCartorio diretamente
</script>

<template>
  <div class="autocomplete-cartorio me-auto user-select-none">
    <!-- Exibir cartório atual quando há apenas um cartório ou não há busca ativa -->
    <div v-if="cartorios.length <= 1" class="cartorio-display">
      <span class="cartorio-text text-white">
        {{ cartorioAtual.nome_fantasia ? cartorioAtual.nome_fantasia + ' - ' : '' }}
        CONT {{ useStore().formatarCns(cartorioAtual.cns) }}
      </span>
    </div>

    <!-- Dropdown para múltiplos cartórios -->
    <div v-else class="cartorio-selector overflow-x-hidden">
      <Dropdown
        v-model="selectedCartorio"
        :options="opcaoCartorios"
        :placeholder="placeholderCartorio"
        :option-label="formatarOpcaoCartorio"
        :pt="dropdownPt"
        data-key="id"
        filter
        filter-placeholder="Pesquisar cartório..."
        empty-message="Nenhum cartório encontrado"
        :show-clear="false"
        append-to="body"
        class="cartorio-dropdown p-0"
        @change="aoSelecionarCartorio"
      >
        <template #option="slotProps">
          <span
            class="rounded-bullet"
            :style="`background-color: ${usuarioStore.getBgCor(slotProps.option.cor)}`"
          />
          <span class="overflow-x-hidden ms-2">{{ formatarOpcaoCartorio(slotProps.option) }}</span>
        </template>

        <template #value="slotProps">
          <div v-if="slotProps.value" class="d-flex align-items-center">
            <span
              class="rounded-bullet"
              :style="`background-color: ${usuarioStore.getBgCor(slotProps.value.cor)}`"
            />
            <span class="overflow-x-hidden ms-2">{{ formatarOpcaoCartorio(slotProps.value) }}</span>
          </div>
        </template>
      </Dropdown>
    </div>
  </div>
</template>

<style>
.rounded-bullet {
  width: 14px;
  height: 14px;
  display: flex;
  border-radius: 50%;
  margin-right: 0.25rem;
}

.autocomplete-cartorio, .cartorio-selector {
  min-width: 600px;
  width: fit-content !important;
}

.cartorio-display .cartorio-text {
  font-size: 1rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cartorio-dropdown {
  min-width: 100%;
}

/* Customizações específicas para o header */
.cartorio-dropdown :deep(.p-dropdown) {
  background: rgba(0, 0, 0, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  min-height: 48px;
}

.cartorio-dropdown :deep(.p-dropdown:focus-within) {
  background: rgba(0, 0, 0, 0.4) !important;
  border-color: var(--cui-primary) !important;
  box-shadow: 0 0 0 0.2rem rgba(var(--cui-primary-rgb), 0.25) !important;
}

.cartorio-dropdown :deep(.p-dropdown-label) {
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 500;
  padding: 0.75rem 1rem;
  font-size: 1rem;
}

.cartorio-dropdown :deep(.p-dropdown-trigger) {
  color: white !important;
  background: transparent !important;
  border: none !important;
}

/* Panel posicionamento */
.cartorio-dropdown :deep(.p-dropdown-panel) {
  z-index: 99999 !important;
}

/* Z-index para overlay */
.p-dropdown-overlay,
.p-dropdown-panel {
  z-index: 99999 !important;
}

.p-select-overlay.p-component {
  z-index: 99999 !important;
  width: 600px;
  overflow-x: scroll;
}
</style>
<style scoped>
</style>
